import { AppContext } from "App";

import { IDataReaderClient } from "@multiweb/clients/dataReader";
import { IUserClient } from "@multiweb/clients/user";
import { IUserSettingsClient } from "@multiweb/clients/userSettings";
import { ISectionLogger } from "@multiweb/logging";
import { IAssignedRole, IAssignedRoles, IOpenedFormHistoryInfo, IUserCompetenceActivity, IUserRoleActivity } from "@multiweb/types/contracts";
import { RequestContext } from "@multiweb/types/multiweb";
import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
import { ASServices } from "OW.Remoting/Basic/ServiceEndpoints";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { ICommunicationManager } from "OW.Remoting/Remoting/ICommunicationManager";
import { ICompetence, IRole } from "OW.Remoting/Remoting/IRolesAndICompetences";
import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { getCancellationTokenAsRequestContext } from "OW.Remoting/Remoting/RequestContext";
import { convertContextFromFrameToRequest } from "UI/UI/Utilities/SharedUIMethods";

/**
 * @internal
 */
export class UserSettingsClient implements IUserSettingsClient {
    //#region Fields
    protected _historyObject: OWObject;
    protected _formHistoryProfileId: number;
    protected readonly _dataReaderClient: IDataReaderClient;
    protected readonly _userClient: IUserClient;
    protected readonly _logger: ISectionLogger;
    protected readonly _communicationManager: ICommunicationManager;

    //#endregion Fields

    //#region Constructors
    constructor(communicationManager: ICommunicationManager, userClient: IUserClient, dataReaderClient: IDataReaderClient, logger: ISectionLogger) {
        this._communicationManager = communicationManager;
        this._userClient = userClient;
        this._dataReaderClient = dataReaderClient;
        this._logger = logger;
    }
    //#endregion Constructors

    //#region Methods
    public async getOpenedFormsHistory(requestContext?: RequestContext): Promise<IOpenedFormHistoryInfo[]> {
        const result = await this._dataReaderClient.getDataRowsWithParams("UserProfile", ["ID", "Data"],
            ":UserProfile.ActualUserProfiles() AND DataType = @0", { params: ["FormHistory"], dataLength: -1, count: 1 }, undefined, requestContext);

        if (result.length === 0)
            return [];

        this._formHistoryProfileId = AttrValueReader.read(result[0]["ID"]);

        const data = result[0]["Data"];
        const parsed = JSON.parse(data as string);

        return this._prepareOpenedFormsHistoryData(parsed);
    }

    public async saveOpenedFormsHistory(history: readonly IOpenedFormHistoryInfo[], requestContext: RequestContext): Promise<void> {
        if (this._historyObject == null)
            this._historyObject = this._formHistoryProfileId ? await OWManager.getObject(this._formHistoryProfileId, "UserProfile", undefined, undefined, undefined, requestContext) : await this._createNewHistory(requestContext);

        const jsonData = JSON.stringify(history);
        await this._historyObject.setValue("Data", jsonData);
        await this._historyObject.applyUpdates();
    }

    public async saveActiveRoles(roles: IUserRoleActivity[], cancellationToken: CancellationToken): Promise<void> {
        const requestContext = getCancellationTokenAsRequestContext(cancellationToken);

        const profile = await this._getRoleSettingsProfile(requestContext);
        const data = this._serializeUserProfileRoles(roles);

        const profileObj = profile?.id ? await OWManager.getObject(profile.id, "UserProfile", undefined, undefined, undefined, requestContext) :
            await OWManager.newObject("UserProfile", undefined, requestContext);

        await profileObj.setValue("User", AppContext.instance.currentUser.id);
        await profileObj.setValue("DataType", "RoleSettingsProfile");
        await profileObj.setValue("Data", JSON.stringify(data));
        await profileObj.applyUpdates();

        profileObj.dispose();
    }

    public async setActiveUserRoleRanges(userRoles: IRole[]): Promise<void> {
        await OWManager.setRoleRanges(this._serializeUserRoleRanges(userRoles));
    }

    public async getActiveRoles(cancellationToken: CancellationToken): Promise<IUserRoleActivity[] | null> {
        return (await this._getRoleSettingsProfile(getCancellationTokenAsRequestContext(cancellationToken)))?.data?.userRoles;
    }

    private async _getRoleSettingsProfile(requestContext?: RequestContext): Promise<IUserDataDTO | null> {
        const result = await this._dataReaderClient.getDataRowsWithParams("UserProfile", ["ID", "Data"],
            ":UserProfile.ActualUserProfiles() AND DataType = @0", { params: ["RoleSettingsProfile"], dataLength: -1 }, undefined, requestContext);
        const profile = result[0];

        if (!profile) {
            return null;
        }

        const profileData = {
            id: AttrValueReader.readRow(profile, "ID"),
            data: JSON.parse(AttrValueReader.readRow(profile, "Data")),
        } as IUserProfileDTO;

        const userRoles = this._deserializeUserProfileRoles(profileData.data.UserRoles);

        return {
            id: profileData.id,
            data: userRoles,
        } as IUserDataDTO;
    }

    /**
     * Přemění data z IUserRoleActivityDTO na IUserRoleDataDTO
     * @param roles Role v IUserRoleActivityDTO podobě
     */
    private _deserializeUserProfileRoles(roles: IUserRoleActivityDTO[]): IUserRoleDataDTO {
        const transformedRolesAndRanges: IUserRoleActivity[] = [];

        for (const { Active, ID, Ranges } of roles) {
            const role = {
                active: Active,
                id: ID,
            } as IUserRoleActivity;

            if (Ranges) {
                role.ranges = [];
                for (const { ID, Active } of Ranges) {
                    const range = {
                        id: ID,
                        active: Active,
                    } as IUserCompetenceActivity;
                    role.ranges.push(range);
                }
            }
            transformedRolesAndRanges.push(role);
        }

        return { userRoles: transformedRolesAndRanges };
    }

    /**
     * Přemění data z kontraktu na DTO
     * @param roles Role v podobě kontraktu
     */
    private _serializeUserProfileRoles(roles: IUserRoleActivity[]): IProfileDataDTO {
        const data = {
            UserRoles: [],
        } as IProfileDataDTO;

        for (const { active, id, ranges } of roles) {
            const role = {
                Active: active,
                ID: id,
            } as IUserRoleActivityDTO;

            if (ranges) {
                role.Ranges = [];
                for (const { active, id } of ranges) {
                    const range = {
                        Active: active,
                        ID: id,
                    } as IUserCompetenceActivityDTO;
                    role.Ranges.push(range);
                }
            }
            data.UserRoles.push(role);
        }
        return data;
    }

    /**
     * Přetransformuje data do DTO struktury.
     * @param userRoles Role s okruhy.
     */
    private _serializeUserRoleRanges(userRoles: IRole[]): IUserActiveRolesDTO[] {
        const roles: IUserActiveRolesDTO[] = [];

        for (const userRole in userRoles) {
            const role = userRoles[userRole] as IRole;
            const ranges: IUserActiveRoleRangesDTO[] = [];
            let competenceRanges: ICompetence[] = [];

            // posílám všechny přidělené role, okruhy pouze u aktivních rolí
            if (role.active && role.competenceRanges && role.competenceRanges.length > 0)
                competenceRanges = role.competenceRanges.filter(range => range.active);

            for (const competenceRange in competenceRanges) {
                const competence = competenceRanges[competenceRange] as ICompetence;
                ranges.push({
                    id: competence.id,
                    name: competence.name,
                    code: competence.code,
                    implicit: competence.implicit,
                    userRoleId: competence.userRoleId,
                });
            }

            roles.push({
                id: role.id,
                userRoleId: role.active ? role.userRoleId : null,
                name: role.name,
                code: role.code,
                implicit: role.implicit,
                competenceRanges: ranges,
            });
        }

        return roles;
    }

    /**
     * Načte přidělené role a kompetence ze služby
     */
    public async getAssignedRoles(cancellationToken: CancellationToken = CancellationToken.none): Promise<IAssignedRoles> {
        const userRoles: IAssignedRolesDTO = await this._communicationManager.callGETRequest("GetLoggedUserRoles", ASServices.owManager, undefined, undefined, convertContextFromFrameToRequest(null, cancellationToken));
        const serializedRoles = {} as IAssignedRoles;
        for (const role of userRoles.roles) {
            serializedRoles[role.id.toString()] = role;
        }
        return serializedRoles;
    }

    private async _createNewHistory(requestContext: RequestContext): Promise<OWObject> {
        this._formHistoryProfileId = await this._userClient.getCurrentUserInfo(requestContext?.cancellationToken).then(user => user.id);

        const historyObject = await OWManager.newObject("UserProfile", undefined, requestContext);
        await historyObject.setValue("User", this._formHistoryProfileId);
        await historyObject.setValue("Name", null);
        await historyObject.setValue("IsDefault", null);
        await historyObject.setValue("FormName", false);
        await historyObject.setValue("ShowInNavigationTree", false);
        await historyObject.setValue("DataType", "FormHistory");
        return historyObject;
    }

    private _prepareOpenedFormsHistoryData(data: object | null | undefined): IOpenedFormHistoryInfo[] {
        if (data == null)
            return [];

        const formHistoryItems = data as IOpenedFormHistoryInfo[];
        const containsInvalidRecords = formHistoryItems.some(item => item.formCode == undefined || item.menuItemId == undefined);
        if (containsInvalidRecords) {
            this._logger.warn("User profile contains invalid form history items (formCode or menuItemId is missing). All history is ignored.", formHistoryItems);
            return [];
        }

        return formHistoryItems;
    }
    //#endregion Methods
}

/**
 * Obsahuje pole přidělených rolí
 * bez informací o aktivnosti
 */
interface IAssignedRolesDTO {
    roles: IAssignedRole[];
}

/**
 * Profil uživatele obsahující jeho data
 */
interface IUserProfileDTO {
    id: number;
    data: IProfileDataDTO;
}

/**
 * Data uživatelského profilu
 * @UserRoles obsahuje strukturu rolí a jejich kompetenčních okruhů
 */
interface IProfileDataDTO {
    UserRoles: IUserRoleActivityDTO[];
}

/**
 * Struktura popisující aktivnost role uživatele.
 */
interface IUserRoleActivityDTO {
    ID: string;
    Active: boolean;
    Ranges?: IUserCompetenceActivityDTO[];
}

/**
 * Struktura popisující aktivnost kompetenčního okruhu.
 */
interface IUserCompetenceActivityDTO {
    ID: string;
    Active: boolean;
}

/**
 * Transformovaná data s výčtem rolí a kompetencí uživatele
 * @userRoles obsahuje strukturu rolí a jejich kompetenčních okruhů
 */
interface IUserRoleDataDTO {
    userRoles: IUserRoleActivity[];
}

/**
 * Transformovaná data uživatele obsahující jeho id a data s rolemi a kompetencemi
 */
interface IUserDataDTO {
    id: number;
    data: IUserRoleDataDTO;
}

export interface IUserActiveRolesDTO {
    id: string;
    userRoleId: string | undefined;
    name: string;
    code: string;
    implicit: boolean;
    competenceRanges?: IUserActiveRoleRangesDTO[];
}

interface IUserActiveRoleRangesDTO {
    id: string;
    name: string;
    code: string;
    implicit: boolean;
    userRoleId: string | undefined;
}
